import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PageHeader } from '../components/AppLayout';
import {
  Plus, Send, Trash2, Loader2, Sparkles, MessageSquare, Pencil, X,
  PanelLeftClose, PanelLeftOpen,
} from '../components/icons';

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now - d) / 60000);
  if (diffMin < 1) return 'à l\'instant';
  if (diffMin < 60) return `il y a ${diffMin}min`;
  if (diffMin < 60 * 24) return `il y a ${Math.floor(diffMin / 60)}h`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

export default function ASAScriptPage() {
  const { isFounder } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [input, setInput] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(() => localStorage.getItem('asa_script_sidebar') !== 'closed');
  const scrollRef = useRef(null);

  const toggleSidebar = () => {
    setSidebarOpen((v) => {
      const n = !v;
      localStorage.setItem('asa_script_sidebar', n ? 'open' : 'closed');
      return n;
    });
  };

  const loadSessions = async () => {
    try {
      const r = await api.get('/asa-script/sessions');
      setSessions(r.data || []);
      if (!activeId && (r.data || []).length > 0) setActiveId(r.data[0].id);
    } catch { toast.error('Erreur chargement sessions'); }
    finally { setLoadingSessions(false); }
  };

  const loadMessages = async (sid) => {
    if (!sid) { setMessages([]); return; }
    setLoadingMessages(true);
    try {
      const r = await api.get(`/asa-script/sessions/${sid}/messages`);
      setMessages(r.data || []);
    } catch { toast.error('Erreur chargement messages'); }
    finally { setLoadingMessages(false); }
  };

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadMessages(activeId); /* eslint-disable-next-line */ }, [activeId]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const createSession = async () => {
    try {
      const r = await api.post('/asa-script/sessions', { title: 'Nouvelle session' });
      setSessions((prev) => [r.data, ...prev]);
      setActiveId(r.data.id);
      setMessages([]);
    } catch { toast.error('Erreur création session'); }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm('Supprimer cette session et son historique ?')) return;
    try {
      await api.delete(`/asa-script/sessions/${sid}`);
      setSessions((prev) => prev.filter((s) => s.id !== sid));
      if (activeId === sid) {
        const remaining = sessions.filter((s) => s.id !== sid);
        setActiveId(remaining[0]?.id || null);
      }
      toast.success('Session supprimée');
    } catch { toast.error('Erreur'); }
  };

  const renameSession = async (sid, currentTitle) => {
    const newTitle = window.prompt('Nouveau titre :', currentTitle);
    if (!newTitle || !newTitle.trim() || newTitle.trim() === currentTitle) return;
    try {
      await api.patch(`/asa-script/sessions/${sid}`, { title: newTitle.trim() });
      setSessions((prev) => prev.map((s) => s.id === sid ? { ...s, title: newTitle.trim() } : s));
    } catch { toast.error('Erreur'); }
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    let sid = activeId;
    if (!sid) {
      // auto-create a session on first send
      try {
        const r = await api.post('/asa-script/sessions', { title: 'Nouvelle session' });
        sid = r.data.id;
        setSessions((prev) => [r.data, ...prev]);
        setActiveId(sid);
      } catch { toast.error('Erreur création session'); return; }
    }
    setSending(true);
    setInput('');
    // Optimistic user message
    const tempId = 'temp-' + Date.now();
    setMessages((prev) => [...prev, { id: tempId, role: 'user', text, created_at: new Date().toISOString() }]);
    try {
      const r = await api.post(`/asa-script/sessions/${sid}/message`, { text });
      // Replace optimistic + append assistant
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== tempId),
        r.data.user_message,
        r.data.assistant_message,
      ]);
      // Update session list order + potential auto-title
      loadSessions();
    } catch (err) {
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      toast.error(err?.response?.data?.detail || 'L\'IA n\'a pas répondu');
      setInput(text);
    } finally { setSending(false); }
  };

  if (!isFounder) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)' }}>
        Réservé aux founders.
      </div>
    );
  }

  const active = sessions.find((s) => s.id === activeId);

  return (
    <div className="page-fade">
      <PageHeader
        title="ASA Script"
        subtitle="Ton copilote pour concepts et scripts viraux — powered by Claude Sonnet"
        right={
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.35)', color: '#A78BFA', fontFamily: 'Sora', fontSize: 10.5, fontWeight: 700 }}>
            <Sparkles size={11} /> Elevate Skill · v1
          </div>
        }
      />

      <div style={{ padding: '0 22px 20px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: `${sidebarOpen ? '260px' : '44px'} 1fr`,
          gap: 14,
          height: 'calc(100vh - 160px)',
          minHeight: 500,
          transition: 'grid-template-columns 0.25s var(--ease-out)',
        }}>
          {/* Left: sessions */}
          {sidebarOpen ? (
            <aside className="asa-card" style={{ display: 'flex', flexDirection: 'column', padding: 10, minHeight: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 6px 6px' }}>
                <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 11, color: 'var(--t2)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Sessions</span>
                <button
                  data-testid="script-sidebar-collapse"
                  onClick={toggleSidebar}
                  title="Réduire"
                  style={{ padding: 4, borderRadius: 6, background: 'transparent', border: 'none', color: 'var(--t3)', cursor: 'pointer' }}
                ><PanelLeftClose size={13} /></button>
              </div>
              <button
                data-testid="script-new-session"
                onClick={createSession}
                className="asa-btn"
                style={{ display: 'inline-flex', justifyContent: 'center', alignItems: 'center', gap: 6, fontSize: 11.5, padding: '9px 12px', margin: '2px 2px 8px' }}
              >
                <Plus size={12} /> Nouvelle session
              </button>
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                {loadingSessions ? (
                  <div style={{ padding: 14, textAlign: 'center', color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 11 }}>
                    Chargement…
                  </div>
                ) : sessions.length === 0 ? (
                  <div style={{ padding: 20, textAlign: 'center', color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 11 }}>
                    Aucune session.<br />Crée-en une pour commencer.
                  </div>
                ) : sessions.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`script-session-${s.id}`}
                    onClick={() => setActiveId(s.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '9px 10px', borderRadius: 9,
                      background: activeId === s.id ? 'rgba(167,139,250,0.12)' : 'transparent',
                      border: '1px solid ' + (activeId === s.id ? '#A78BFA55' : 'transparent'),
                      cursor: 'pointer', transition: 'all 0.12s',
                    }}
                    onMouseEnter={(e) => { if (activeId !== s.id) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                    onMouseLeave={(e) => { if (activeId !== s.id) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <MessageSquare size={12} color={activeId === s.id ? '#A78BFA' : 'var(--t3)'} strokeWidth={2} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontFamily: 'Sora', fontWeight: activeId === s.id ? 700 : 600, fontSize: 12,
                        color: 'var(--t1)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{s.title}</div>
                      <div style={{ fontFamily: 'Lexend', fontSize: 9.5, color: 'var(--t3)', marginTop: 1 }}>
                        {fmtDate(s.updated_at)}
                      </div>
                    </div>
                    <button
                      data-testid={`script-rename-${s.id}`}
                      onClick={(e) => { e.stopPropagation(); renameSession(s.id, s.title); }}
                      title="Renommer"
                      style={{ padding: 3, borderRadius: 5, background: 'transparent', border: 'none', color: 'var(--t3)', cursor: 'pointer', opacity: activeId === s.id ? 1 : 0.5 }}
                    ><Pencil size={10} /></button>
                    <button
                      data-testid={`script-delete-${s.id}`}
                      onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                      title="Supprimer"
                      style={{ padding: 3, borderRadius: 5, background: 'transparent', border: 'none', color: '#FF6B6B', cursor: 'pointer', opacity: activeId === s.id ? 1 : 0.4 }}
                    ><Trash2 size={10} /></button>
                  </div>
                ))}
              </div>
            </aside>
          ) : (
            <button
              data-testid="script-sidebar-expand"
              onClick={toggleSidebar}
              title="Ouvrir les sessions"
              style={{
                width: 36, padding: '10px 6px', borderRadius: 10,
                background: 'var(--sf)', border: '1px solid var(--bd)',
                color: 'var(--t2)', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
                alignSelf: 'flex-start',
              }}
            >
              <PanelLeftOpen size={14} />
              <span style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', fontFamily: 'Sora', fontWeight: 700, fontSize: 10, letterSpacing: '0.08em' }}>SESSIONS</span>
            </button>
          )}

          {/* Right: chat */}
          <div className="asa-card" style={{ display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0, overflow: 'hidden' }}>
            {/* Chat header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px', borderBottom: '1px solid var(--bd)' }}>
              <div style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(167,139,250,0.15)', border: '1.5px solid #A78BFA55', display: 'grid', placeItems: 'center' }}>
                <Sparkles size={13} color="#A78BFA" strokeWidth={2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: 'var(--t1)' }}>
                  {active?.title || 'ASA Script'}
                </div>
                <div style={{ fontFamily: 'Lexend', fontSize: 10.5, color: 'var(--t3)' }}>
                  Concepts viraux · réécriture de scripts · fiches montage
                </div>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              data-testid="script-messages"
              style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              {loadingMessages ? (
                <div style={{ textAlign: 'center', color: 'var(--t3)', fontFamily: 'Lexend', fontSize: 12, padding: 30 }}>
                  Chargement…
                </div>
              ) : messages.length === 0 && !sending ? (
                <EmptyState onSuggest={(text) => setInput(text)} />
              ) : (
                messages.map((m) => <MessageBubble key={m.id} msg={m} />)
              )}
              {sending && <TypingIndicator />}
            </div>

            {/* Composer */}
            <form
              onSubmit={send}
              style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--bd)', background: 'var(--sf2)' }}
            >
              <textarea
                data-testid="script-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Ex : Donne-moi 10 concepts viraux pour un coach en immobilier. (Shift+Entrée = nouvelle ligne)"
                rows={2}
                style={{
                  flex: 1, resize: 'none', padding: '10px 12px',
                  background: 'var(--sf)', border: '1px solid var(--bd)', borderRadius: 10,
                  color: 'var(--t1)', fontFamily: 'Lexend', fontSize: 13,
                  outline: 'none', minHeight: 44, maxHeight: 160,
                }}
              />
              <button
                type="submit"
                data-testid="script-send"
                disabled={!input.trim() || sending}
                className="asa-btn"
                style={{
                  width: 46, minWidth: 46, padding: 0,
                  background: input.trim() ? 'linear-gradient(135deg, #A78BFA, #7C63E0)' : 'var(--sf)',
                  border: 'none', color: '#fff',
                  display: 'grid', placeItems: 'center',
                }}
              >
                {sending ? <Loader2 size={16} className="spin" /> : <Send size={15} />}
              </button>
            </form>
          </div>
        </div>
      </div>
      <style>{`
        .spin { animation: rotate 0.9s linear infinite; }
        @keyframes rotate { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === 'user';
  return (
    <div
      data-testid={`script-msg-${msg.id}`}
      style={{
        display: 'flex', gap: 10,
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
      }}
    >
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: isUser ? 'rgba(46,163,242,0.15)' : 'rgba(167,139,250,0.15)',
        border: '1.5px solid ' + (isUser ? '#2EA3F255' : '#A78BFA55'),
        display: 'grid', placeItems: 'center',
        fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5,
        color: isUser ? '#2EA3F2' : '#A78BFA',
      }}>{isUser ? 'Toi' : 'AI'}</div>
      <div style={{
        maxWidth: '76%',
        padding: '10px 14px', borderRadius: 12,
        background: isUser ? 'rgba(46,163,242,0.10)' : 'var(--sf2)',
        border: '1px solid ' + (isUser ? '#2EA3F244' : 'var(--bd)'),
      }}>
        <div style={{
          fontFamily: 'Lexend', fontSize: 13, color: 'var(--t1)',
          lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>{msg.text}</div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        background: 'rgba(167,139,250,0.15)', border: '1.5px solid #A78BFA55',
        display: 'grid', placeItems: 'center',
        fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: '#A78BFA',
      }}>AI</div>
      <div style={{
        padding: '10px 14px', borderRadius: 12,
        background: 'var(--sf2)', border: '1px solid var(--bd)',
        display: 'inline-flex', alignItems: 'center', gap: 7,
      }}>
        <span className="dot" /><span className="dot d2" /><span className="dot d3" />
        <style>{`
          .dot { width:6px; height:6px; border-radius:50%; background:#A78BFA; opacity:0.4; animation: pulse 1.2s ease-in-out infinite; }
          .dot.d2 { animation-delay: 0.2s; }
          .dot.d3 { animation-delay: 0.4s; }
          @keyframes pulse { 0%,100% { opacity:0.3; transform:scale(0.85); } 50% { opacity:1; transform:scale(1); } }
        `}</style>
      </div>
    </div>
  );
}

function EmptyState({ onSuggest }) {
  const suggestions = [
    '10 concepts viraux pour un coach en business (varie les catégories).',
    'Réécris ce script pour le rendre plus viral et oral : "Voici pourquoi la plupart des gens..."',
    'Concepts newsjacking pour un restaurateur — mais vérifie l\'actualité avant.',
    'Fais-moi une fiche montage complète sur "Pourquoi les meilleurs closers ne closent jamais".',
  ];
  return (
    <div style={{ textAlign: 'center', padding: '30px 20px', maxWidth: 600, margin: '0 auto' }}>
      <div style={{
        width: 60, height: 60, borderRadius: 16, margin: '0 auto 14px',
        background: 'linear-gradient(135deg, rgba(167,139,250,0.25), rgba(46,163,242,0.15))',
        border: '1.5px solid rgba(167,139,250,0.4)',
        display: 'grid', placeItems: 'center',
      }}>
        <Sparkles size={26} color="#A78BFA" strokeWidth={1.8} />
      </div>
      <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 18, color: 'var(--t1)', marginBottom: 5 }}>
        Bienvenue dans ASA Script
      </div>
      <div style={{ fontFamily: 'Lexend', fontSize: 12.5, color: 'var(--t3)', marginBottom: 22, lineHeight: 1.5 }}>
        Génère des concepts et des scripts viraux pour n&apos;importe quelle niche. Ton copilote utilise le skill Elevate Influence.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7, textAlign: 'left' }}>
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onSuggest(s)}
            style={{
              padding: '10px 13px', borderRadius: 10,
              background: 'var(--sf)', border: '1px solid var(--bd)',
              color: 'var(--t2)', fontFamily: 'Lexend', fontSize: 12,
              cursor: 'pointer', transition: 'all 0.12s',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#A78BFA88'; e.currentTarget.style.color = 'var(--t1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.color = 'var(--t2)'; }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
